import React, { useState, useContext, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useToastContext from 'hooks/useToastContext'
import AuthContext from 'context/AuthContext'
import { apiPost } from 'utils/apiFetch'
import apiPath from 'utils/pathObj'
import ErrorMessage from 'components/ErrorMessage'
import OInputField from 'components/reusable/OInputField'
import formValidation from 'utils/formValidation'
import LiveFormPreview from './Preview'

const FIELD_TYPES = ['heading','input', 'radio', 'checkbox', 'date', 'file', 'select', 'textarea']

const AddOnboarding = ({ getForms, closeModal }) => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const { user } = useContext(AuthContext)

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    reset,
    watch,
  } = useForm({ mode: 'onChange' })

  const [category, setCategory] = useState('')
  const [customFields, setCustomFields] = useState([])
  const [fieldType, setFieldType] = useState('')
  const [fieldLabel, setFieldLabel] = useState('')
  const [fieldOptions, setFieldOptions] = useState('')
  const [fieldValues, setFieldValues] = useState({});

  const [alignment, setAlignment] = useState('');
  const [width, setWidth] = useState('');
  const [flexDirection, setFlexDirection] = useState('');
  const [customClass, setCustomClass] = useState('');
  const [dynamicFields, setDynamicFields] = useState([
    { fieldType: '', fieldLabel: '', fieldOptions: '', mandatory:false},
  ])
  

  const watchFields = watch();

  const formName = watch('formName');
  useEffect(() => {
    console.log('Form name changed:', formName);
  }, [formName]);
  
  const handleDynamicFieldChange = (index, key, value) => {
    setDynamicFields(prev => {
      const newFields = [...prev];
      newFields[index][key] = value;
      return newFields;
    });
  }
  
  const addDynamicFieldBlock = () => {
    setDynamicFields(prev => [...prev, { fieldType: '', fieldLabel: '', fieldOptions: '' , mandatory: false}]);
  }
  
  const handleAddField = () => {
    const newFields = dynamicFields.map(field => {
      const { fieldType, fieldLabel, fieldOptions, mandatory } = field;
  
      if (!fieldType || !fieldLabel.trim()) {
        notification.error('Please fill all field blocks with type and label');
        return null;
      }
  
      return {
        id: Date.now() + Math.random(),
        type: fieldType,
        label: fieldLabel.trim(),
        mandatory: mandatory || false,
        options: ['radio', 'select', 'checkbox'].includes(fieldType)
          ? fieldOptions.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0)
          : [],
        styles: {
          container: `mb-4 flex ${flexDirection} ${width} ${alignment} ${customClass}`,
          label: `text-sm font-medium mr-2 ${alignment} ${width}`,
          input: `border border-gray-300 rounded-md shadow-sm p-1 ${width} ${alignment}`,
        },
        value: fieldType === 'checkbox' ? [] : '',
      };
    }).filter(Boolean);
  
    if (newFields.length !== dynamicFields.length) return;
  
    // Push as a group (array of fields)
    setCustomFields(prev => [...prev, newFields]);
  
    // Reset dynamic fields
    setDynamicFields([{ fieldType: '', fieldLabel: '', fieldOptions: '', mandatory: false }]);
    setAlignment('');
    setWidth('');
    setFlexDirection('');
    setCustomClass('');
  };
  

  const handleRemoveField = (id) => {
    setCustomFields(prev =>
      prev
        .map(group => group.filter(field => field.id !== id))
        .filter(group => group.length > 0) // Remove empty groups
    );
  
    setFieldValues(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };
  
  const handleCategoryChange = (e) => {
    const val = e.target.value
    if (val) {
      setCategory(val)
      clearErrors('category')
    } else {
      setCategory('')
      setError('category', {
        type: 'manual',
        message: 'Please select a category.'
      })
    }
  }
      console.log(customFields, "custom fielsddd")

      
      const onSubmit = async (data) => {
        // 1. Validate required fields
        if (!category) {
          setError('category', {
            type: 'manual',
            message: 'Please select a category.'
          });
          return;
        }
      
        // 2. Prepare FormData
        const formData = new FormData();
      
        // 3. Clone customFields while preserving File objects  
        const clonedCustomFields = customFields.map(row => 
          row.map(field => {
            if (field.type === 'file' && field.value instanceof File) {
              return {
                ...field,
                uploadKey: `file_${field.id}` // Add upload reference
              };
            }
            return {...field}; // Clone other fields normally
          })
        );
      
        // 4. Process files and append to FormData
        let fileCount = 0;
        clonedCustomFields.forEach(row => {
          row.forEach(field => {
            if (field.type === 'file' && field.value instanceof File) {
              console.log(`[8] Appending file ${field.uploadKey}:`, field.value);
              formData.append(field.uploadKey, field.value);
              fileCount++;
            }
          });
        });
      
        // 5. Prepare clean version for JSON (without File objects)
        const fieldsForJson = clonedCustomFields.map(row => 
          row.map(field => {
            if (field.type === 'file') {
              const {value, ...rest} = field;
              return rest;
            }
            return field;
          })
        );
      
        // 6. Append other form data
        formData.append('formName', data.formName);
        formData.append('formType', data.formType);
        formData.append('category', category);
        formData.append('status', 'active');
        formData.append('customFields', JSON.stringify(fieldsForJson));
      
        for (let [key, value] of formData.entries()) {
          console.log(
            key, 
            value instanceof File 
              ? `[File: ${value.name}, ${value.size} bytes, ${value.type}]` 
              : value
          );
        }
      
        // 7. Submit to API
        try {
          const res = await apiPost(apiPath.addForm, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          });
      
          // 8. Handle response
          if (res?.data?.success) {
            notification.success(res?.data?.message);
            closeModal();
          } else {
            console.error("API returned error:", res?.data);
            notification.error(res?.data?.message || 'Submission failed');
          }
        } catch (err) {
          console.error("Submission failed:", {
            error: err,
            response: err.response?.data,
            stack: err.stack
          });
          
          notification.error(
            err.response?.data?.message || 
            'An error occurred while saving the form'
          );
        }
      };
  
  return (
    <div className='justify-end items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none pt-12 pr-10'>
                <div className='relative w-auto mx-auto max-w-7xl'>
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7 ml-[133px]'>
                    <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[862px]'>
                    <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
                <h3 className='text-xl font-semibold'>{t('ADD_FORM')}</h3>
                <button
                  className='ml-auto text-black border-2 rounded-full h-8 w-8 flex items-center justify-center outline-none focus:outline-none'
                  onClick={closeModal}
                  title='Close'
                >
                  <span className='text-[#B8BBBF] text-2xl'>×</span>
                </button>
              </div>
            {/* Body */}
            <form onSubmit={handleSubmit(onSubmit)} className='flex-auto relative p-6 grid grid-cols-2 gap-6'>

              {/* Left Column */}
              <div>
                <div className='grid sm:grid-cols-1 gap-4'>
                  <div className='px-2'>
                    <OInputField
                      type='text'
                      name='formName'
                      id='formName'
                      inputLabel={<>{t('Title')}<span className='text-red-500'>*</span></>}
                      register={register('formName', formValidation.name)}
                      errors={errors}
                    />
                  </div>
                  <div className='px-2'>
                    <OInputField
                      type='text'
                      name='formType'
                      id='formType'
                      inputLabel={<>{t('TYPE')}<span className='text-red-500'>*</span></>}
                      register={register('formType', formValidation.name)}
                      errors={errors}
                    />
                  </div>
                  <div className='px-2'>
                    <div className='relative w-full z-0 group'>
                      <select
                        id='category'
                        className='block py-4 px-3 w-full text-sm border-2 rounded-lg border-[#DFDFDF]'
                        value={category}
                        onChange={handleCategoryChange}
                      >
                        <option value=''>{t('SELECT_CATEGORY')}</option>
                        <option key='onboarding' value='onboarding'>{t('ONBOARDING')}</option>
                      </select>
                      <label className='absolute text-sm text-[#A5A5A5] transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10'>
                        {t('CATEGORY')}<span className='text-red-500'>*</span>
                      </label>
                      <ErrorMessage message={errors?.category?.message} />
                    </div>
                  </div>
                </div>
                {/* Custom Fields */}
                <div className='mt-6 border-t pt-6'>
                  <h4 className='font-medium mb-4'>{t('Add Custom Fields')}</h4>
                  {dynamicFields.map((field, index) => (
                    <div key={index} className='mb-4 border p-3 rounded'>
                      <div className='flex items-center mb-2 gap-2'>
                        <select
                          value={field.fieldType}
                          onChange={e => handleDynamicFieldChange(index, 'fieldType', e.target.value)}
                          className='border px-3 py-2 rounded w-[140px]'>
                          <option value=''>Select Type</option>
                          {FIELD_TYPES.map(type => (
                            <option key={type} value={type}>{type.toUpperCase()}</option>
                          ))}
                        </select>
                        <input
                          type='text'
                          placeholder='Field Label'
                          value={field.fieldLabel}
                          onChange={e => handleDynamicFieldChange(index, 'fieldLabel', e.target.value)}
                          className='border px-3 py-2 rounded w-[200px]'
                        />
                       <div className="flex flex-col items-center font-sm ml-1">
                        <label className="text-xs mb-1 text-center">Required</label>
                        <input
                          type="checkbox"
                          checked={field.mandatory}
                          onChange={(e) => handleDynamicFieldChange(index, 'mandatory', e.target.checked)}
                          className="h-4 w-4"
                        />
                      </div>
                      </div>
                      {['radio', 'select', 'checkbox'].includes(field.fieldType) && (
                        <div className='flex flex-col gap-1 mb-2'>
                          <label className='font-medium'>Options (comma separated)</label>
                          <input
                            type='text'
                            placeholder='e.g. Option1, Option2'
                            value={field.fieldOptions}
                            onChange={e => handleDynamicFieldChange(index, 'fieldOptions', e.target.value)}
                            className='border px-3 py-2 rounded w-[300px]'
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    type='button'
                    onClick={addDynamicFieldBlock}
                    className='bg-green-500 text-white px-4 py-2 rounded mt-2 mb-2'
                  >
                    Add Another Field
                  </button>
                  <div className="flex gap-4 items-center mb-4">
                  <div>
                  <label className="block mb-1 ml-2">Label Alignment</label>
                  <select
                    value={alignment}
                    onChange={(e) => setAlignment(e.target.value)}
                    className="border p-2 w-full"
                  >
                    <option value="">Set an Alignment</option>
                    <option key="justify-start" value="justify-start">Left</option>
                    <option key="justify-center" value="justify-center">Center</option>
                    <option key="justify-end" value="justify-end">Right</option>
                  </select>
                </div>
                <div>
                <label className="block mb-1 ml-2">Width</label>
                <select value={width} onChange={(e) => setWidth(e.target.value)} className="border p-2 w-full">
                <option value="">Set a Width</option>
                  <option key="w-full" value="w-full">Full Width</option>
                  <option key="w-1/2" value="w-1/2">Half Width</option>
                  <option key="w-1/3" value="w-1/3">One Third</option>
                  <option key="w-1/4" value="w-1/4">One Fourth</option>
                </select>
                  </div>
                </div>
                <div>
                <div>
                  <label className="block mb-1">Flex Direction</label>
                  <select value={flexDirection} onChange={(e) => setFlexDirection(e.target.value)} className="border p-2 w-full">
                  <option value="">Set a Direction</option>
                    <option key="flex-col" value="flex-col">Column</option>
                    <option key="flex-row" value="flex-row">Row</option>
                  </select>
                </div>
                <div className="col-span-full mt-2">
                  <label className="block mb-1">Custom Class (Optional)</label>
                  <input value={customClass} onChange={(e) => setCustomClass(e.target.value)} className="border p-2 w-full" placeholder="e.g. justify-end items-end" />
                </div>
                </div>
                  <button
                    type='button'
                    onClick={handleAddField}
                    className='bg-blue-500 text-white px-4 py-2 rounded mt-2'>
                    Add Field
                  </button>
                </div>
              </div>
              <LiveFormPreview
                customFields={customFields}
                setCustomFields={setCustomFields}
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                handleRemoveField={handleRemoveField}
              />
            </form>

            {/* Footer */}
            <div className='flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b gap-4'>
              <button
                className='bg-gray-500 text-white active:bg-gray-700 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none'
                type='button'
                onClick={closeModal}
              >
                Back
              </button>
              <button
                className='bg-blue-600 text-white active:bg-blue-800 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none'
                type='submit'
                onClick={handleSubmit(onSubmit)}
              >
                Save Form
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddOnboarding


